import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const goTo = (id) => (e) => {
  e.preventDefault();
  const el = document.querySelector(id);
  if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -90 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

// Stage 1 (space opening, copy A) + stage 2 (cloud transition statement, copy B).
export const SpaceCopy = ({ trackRef, reduced }) => {
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start start", "end end"], layoutEffect: false });
  const p = scrollYProgress;

  const o1 = useTransform(p, [0.06, 0.16], [1, 0]);
  const y1 = useTransform(p, [0.06, 0.16], [0, -56]);
  const cueO = useTransform(p, [0.02, 0.08], [1, 0]);

  // restrained cloud statement — appears and fades with the transition
  const o2 = useTransform(p, [0.36, 0.44, 0.55, 0.62], [0, 1, 1, 0]);
  const s2 = useTransform(p, [0.36, 0.62], [0.96, 1.04]);
  const deck = useTransform(p, [0.3, 0.56], ["scaleX(0)", "scaleX(1)"]);

  const rise = (delay) => ({
    initial: reduced ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1, delay, ease: EASE },
  });

  return (
    <>
      <motion.div style={{ opacity: cueO }} className="absolute left-5 top-24 z-20 sm:left-8 sm:top-28 lg:left-12">
        <motion.div {...rise(1.6)} data-testid="scroll-to-begin-indicator" className="flex items-center gap-3 font-mono text-[10px] font-medium tracking-[0.3em] text-white/85 sm:text-[11px]">
          SCROLL TO BEGIN
          <ArrowDown className="h-3.5 w-3.5 animate-scroll-cue" />
        </motion.div>
      </motion.div>

      {/* opening copy — bottom left */}
      <motion.div style={{ opacity: o1, y: y1 }} className="absolute bottom-0 left-0 z-20 px-5 pb-24 sm:px-8 sm:pb-16 lg:px-12 lg:pb-20">
        <motion.p {...rise(0.9)} className="eyebrow mb-5 !text-[#FFC48C]">
          <span className="rule !bg-[#FFC48C]" />
          PRECISION MACHINED PRODUCTS
        </motion.p>
        <h1 data-testid="orbital-headline" className="headline text-[10.5vw] text-white sm:text-6xl lg:text-[4.6rem] xl:text-[5.2rem]">
          {["Big ideas depend", "on the smallest details."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="block"
                initial={reduced ? false : { y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.3, delay: 0.45 + i * 0.15, ease: EASE }}
              >
                {i === 1 ? <>on the <span className="font-serif-i">smallest details.</span></> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p {...rise(1.25)} data-testid="orbital-subline" className="mt-5 max-w-lg text-[15px] leading-relaxed text-white/85 sm:text-base [text-wrap:pretty]">
          Your design has a job to do. Every surface, feature, and fit plays a part.
        </motion.p>
        <motion.p {...rise(1.35)} className="mt-2 max-w-lg text-[15px] leading-relaxed text-white/70 sm:text-base [text-wrap:pretty]">
          PMP brings precision machining and assembly together to turn demanding designs into components built for their application.
        </motion.p>
        <motion.div {...rise(1.5)} className="mt-6 flex flex-wrap items-center gap-3">
          <a href="#contact" onClick={goTo("#contact")} data-testid="cta-request-quote" className="btn btn-light">Request a Quote <ArrowRight className="h-4 w-4" /></a>
          <a href="#capabilities" onClick={goTo("#capabilities")} data-testid="cta-explore-capabilities" className="btn btn-ghost">Explore Our Capabilities</a>
        </motion.div>
        <motion.p {...rise(1.65)} className="mt-6 font-mono text-[10.5px] tracking-[0.14em] text-white/60">
          FROM PROTOTYPE TO PRODUCTION. FROM COMPONENT TO ASSEMBLY.
        </motion.p>
      </motion.div>

      {/* cloud transition statement */}
      <motion.div style={{ opacity: o2, scale: s2 }} data-testid="cloud-statement" className="pointer-events-none absolute inset-x-0 bottom-[16vh] z-20 flex flex-col items-center px-6 text-center sm:bottom-[14vh]">
        <p className="headline text-3xl text-white sm:text-5xl lg:text-6xl">
          The part may be small.
        </p>
        <p className="font-serif-i mt-2 text-3xl text-[#FFC48C] sm:mt-3 sm:text-5xl lg:text-6xl">
          What depends on it isn’t.
        </p>
        <div className="mt-6 flex items-center gap-3 font-mono text-[10px] tracking-[0.26em] text-white/70">
          CLOUD DECK
          <span className="relative h-px w-24 overflow-hidden bg-white/25">
            <motion.span style={{ scaleX: deck }} className="absolute inset-0 origin-left bg-[#FFC48C]" />
          </span>
        </div>
      </motion.div>
    </>
  );
};
